/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect } from 'react'
import { useServiceContext } from '../../../../root'
import { useRcmTableData } from '../../../../hooks/useRcmTableData'
import { RcmTable } from '../../../rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell } from '../../../table/TableComponents'
import ActionsMenu from '../../../shared/ActionsMenu'
import { Link } from 'react-router'
import { useFsiActionMenu } from '../../../../hooks/useFsiActionMenu'
import DueDateIndicator from '../../../DueDateIndicator'
import DocIcon from '../../../shared/DocIcon'
import DocState from '../../../shared/DocState'
import ValidDocIndicator from '../../../shared/ValidDocIndicator'
import { FsiType, AclLevel, SystemFolderType } from '../../../../constants/fsi'
import { ReportActionMenu } from '../reports/ReportActionMenu'
import { getFileDownloadUrl } from '../../../../services/api/files'

// General document action menu (for REGULATED_CONTENT, INIT_ETMF_FILES, FORMS, TEMPLATES)
const DocumentActionMenu: React.FC<{ item: any }> = ({ item }) => {
  const { items, setIsOpen } = useFsiActionMenu(item, {})

  if (items.length === 0) {
    items.push({ value: 'ACTION_MENU_NO_ACTION_AVAILABLE', disabled: true })
  }

  return (
    <ActionsMenu
      menuId={`favorite-doc-${item.id}`}
      onToggle={(open: boolean) => {
        setIsOpen(open)
      }}
      items={items}
      isBigMenu={false}
    />
  )
}

// Determines which action menu to render based on systemFolderType (matching legacy behavior)
const FavoriteItemActionMenu: React.FC<{ row: any }> = ({ row }) => {
  const documentFolderTypes = [
    SystemFolderType.REGULATED_CONTENT,
    SystemFolderType.INIT_ETMF_FILES,
    SystemFolderType.FORMS,
    SystemFolderType.TEMPLATES
  ]

  if (documentFolderTypes.includes(row.systemFolderType)) {
    return <DocumentActionMenu item={row} />
  }

  return <ReportActionMenu fsi={row} />
}

interface FavoritesDocsTableProps {
  hidePaginate?: boolean
}

export const FavoritesDocsTable: React.FC<FavoritesDocsTableProps> = ({ hidePaginate }) => {
  const {
    service: {
      filter: { translate, date },
      init
    }
  } = useServiceContext()
  const tableData = useRcmTableData()
  const isEtmfEnabled = init.getFeaturesInfo()?.etmfFeature?.show || false

  useEffect(() => {
    // Load data using context
    tableData.refreshTable()
    const handleUpdate = () => {
      tableData.refreshTable()
    }

    // Listen for a custom event (e.g., 'updateFavoritesTable')
    window.addEventListener('updateFavorites', handleUpdate)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('updateFavorites', handleUpdate)
    }
  }, [])

  return (
    <RcmTable
      entries={tableData.entries}
      isLoading={tableData.isLoading}
      hidePaginate={hidePaginate}
      TableFooter={
        <tr>
          <td colSpan={3} className="view-all">
            <Link to="/documents/favorites">{translate('VIEW_ALL')}</Link>
          </td>
        </tr>
      }
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="favorites-name">{translate('NAME')}</TableHeadCell>
          <TableHeadCell id="favorites-updated">{translate('TABLE_TH_UPDATED_ON')}</TableHeadCell>
          <TableHeadCell id="favorites-actions">{translate('ACTIONS')}</TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }) => (
        <tr key={row.id}>
          <td className="name clipped-cell with-thumb">
            <div className="d-flex align-left">
              {row.type !== FsiType.FOLDER && <ValidDocIndicator fsi={row} />}
              <DocIcon
                fsi={row}
                onClickDownload={
                  row.isReport
                    ? async () => {
                        try {
                          const data = await getFileDownloadUrl(Number(row.id))
                          if (data.url) {
                            window.location.href = data.url
                          }
                        } catch (error) {
                          console.error('Error downloading report:', error)
                        }
                      }
                    : undefined
                }
                inWorkflow={row.workflow && row.workflow.length > 0}
              />
              <div className="ellipsis" title={row.name}>
                {row.type === FsiType.PLACEHOLDER ||
                (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
                !row.valid ? (
                  <span>
                    {row.name}
                    {isEtmfEnabled && row.type === FsiType.PLACEHOLDER && (
                      <DueDateIndicator dueDateStatus={row.dueDateStatus} translate={translate} />
                    )}
                  </span>
                ) : row.isReport ? (
                  <button
                    type="button"
                    className="text-link-btn word-break btn btn-link"
                    onClick={async () => {
                      try {
                        const data = await getFileDownloadUrl(Number(row.id))
                        if (data.url) {
                          window.location.href = data.url
                        }
                      } catch (error) {
                        console.error('Error downloading report:', error)
                      }
                    }}
                  >
                    {row.name}
                  </button>
                ) : row.type === FsiType.FOLDER ? (
                  <Link to={`/browser/${row.id}?type=${row.systemFolderType}&page=1`}>{row.name}</Link>
                ) : (
                  <Link to={`/show-file/${row.id}`}>{row.name}</Link>
                )}
              </div>
              <DocState fsi={row} />
            </div>
          </td>
          <td className="narrow updated">{row.updatedOn ? date(row.updatedOn) : translate('NOT_AVAILABLE')}</td>
          <td className="actions">{row.valid && <FavoriteItemActionMenu row={row} />}</td>
        </tr>
      )}
    />
  )
}
